import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import { AssignmentReturned, Close } from '@material-ui/icons';
import { useHistory } from 'react-router';
import CardTitle from '../../../common/card/CardTitle';
import {
  getProductEntityIdPrefix,
  getProductEntityName
} from '../../products/Product.config';
import { useAssignRiskReferences, useFindRiskReferences } from '../Risk.hook';
import { ApiParams } from '../../../common/api/api';
import AssignUserNeedDialog from '../../products/dialogs/AssignUserNeedDialog';
import {
  ProductDesignInput,
  ProductDesignOutput,
  ProductUserNeed
} from '../../products/Product.interface';
import AssignDesignInputDialog from '../../products/dialogs/AssignDesignInputDialog';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import { AppSnackbar, ConfirmDialog } from '@sierralabs/react-material-ui';
import AssignDesignOutputDialog from '../../products/dialogs/AssignDesignOutputDialog';

export interface RiskReferenceCardProps {
  riskId: number;
  target: 'user-needs' | 'design-inputs' | 'design-outputs';
  disabled?: boolean;
}

const RiskReferenceCard: React.FC<RiskReferenceCardProps> = ({
  riskId,
  target,
  disabled
}) => {
  const history = useHistory();
  const [openDialog, setOpenDialog] = useState(false);
  const [order, setOrder] = useState<string>();
  const [orderDirection, setOrderDirection] = useState<'ASC' | 'DESC'>();
  const handleSort = (column: string) => {
    setOrder(column);
    setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
  };
  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
  };
  const {
    updatedSource,
    isAssigningReferences,
    assigningReferenceError,
    assignReferences
  } = useAssignRiskReferences(riskId, target);
  const {
    references,
    isLoadingReferences,
    loadingReferenceError,
    findReferences
  } = useFindRiskReferences(riskId, target);

  useEffect(() => {
    const params: ApiParams = {};
    if (order) params.order = order;
    if (orderDirection) params.direction = orderDirection;
    findReferences(params);
  }, [order, orderDirection, updatedSource, findReferences]);

  const reference = references?.[0];
  const [unassign, setUnassign] = useState<typeof reference>();

  let displayName = '',
    queryAlias = '',
    idPrefix = 'ID';

  switch (target) {
    case 'user-needs':
      displayName = getProductEntityName('userNeed');
      queryAlias = 'userNeed';
      idPrefix = getProductEntityIdPrefix('userNeed');
      break;
    case 'design-inputs':
      displayName = getProductEntityName('designInput');
      queryAlias = 'designInput';
      idPrefix = getProductEntityIdPrefix('designInput');
      break;
    case 'design-outputs':
      displayName = getProductEntityName('designOutput');
      queryAlias = 'designOutput';
      idPrefix = getProductEntityIdPrefix('designOutput');
      break;
  }
  return (
    <Card>
      <CardTitle>
        <AssignmentReturned />
        {displayName} Reference
      </CardTitle>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={24}>
                <TableSortLabel
                  active={order === `${queryAlias}.id`}
                  direction={getSortDirection(`${queryAlias}.id`)}
                  onClick={() => handleSort(`${queryAlias}.id`)}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === `${queryAlias}.title`}
                  direction={getSortDirection(`${queryAlias}.title`)}
                  onClick={() => handleSort(`${queryAlias}.title`)}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.status'}
                  direction={getSortDirection('task.status')}
                  onClick={() => handleSort('task.status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(isLoadingReferences || isAssigningReferences) && (
              <TableRow>
                <TableCell colSpan={4} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {references &&
              references.map(reference => (
                <TableRow
                  key={reference.id}
                  hover
                  onClick={() => {
                    history.push(
                      `/portal/products/${
                        (reference as any).productId
                      }/${target}/${reference.id}`
                    );
                  }}
                >
                  <TableCell>{`${idPrefix}-${reference.id}`}</TableCell>
                  <TableCell>{reference.title}</TableCell>
                  <TableCell>
                    <TaskStatusLabel task={reference.task} />
                  </TableCell>
                  <TableCell style={{ textAlign: 'center' }}>
                    <IconButton
                      size='small'
                      disabled={disabled}
                      onClick={event => {
                        event.stopPropagation();
                        setUnassign(reference);
                      }}
                    >
                      <Close />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            <TableRow>
              <TableCell colSpan={2}>
                <Button
                  variant='outlined'
                  color='primary'
                  disabled={disabled}
                  onClick={() => setOpenDialog(true)}
                >
                  Assign {displayName}
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {openDialog &&
        references &&
        (target === 'user-needs' ? (
          <AssignUserNeedDialog
            open
            selectedItems={references as ProductUserNeed[]}
            onCancel={() => setOpenDialog(false)}
            onSubmit={userNeeds => {
              setOpenDialog(false);
              assignReferences(userNeeds.map(un => un.id));
            }}
          />
        ) : target === 'design-inputs' ? (
          <AssignDesignInputDialog
            open
            selectedItems={references as ProductDesignInput[]}
            onCancel={() => setOpenDialog(false)}
            onSubmit={designInputs => {
              setOpenDialog(false);
              assignReferences(designInputs.map(di => di.id));
            }}
          />
        ) : (
          target === 'design-outputs' && (
            <AssignDesignOutputDialog
              open
              selectedItems={references as ProductDesignOutput[]}
              onCancel={() => setOpenDialog(false)}
              onSubmit={designInputs => {
                setOpenDialog(false);
                assignReferences(designInputs.map(di => di.id));
              }}
            />
          )
        ))}
      {unassign && (
        <ConfirmDialog
          open
          title='Are you sure?'
          message={`Are you sure you want to un-assign "${unassign.title}"?`}
          onCancel={() => setUnassign(undefined)}
          onConfirm={() => {
            if (!references) return;
            const updatedReferenceIds = references
              .map(r => r.id)
              .filter(id => id !== unassign.id);
            setUnassign(undefined);
            assignReferences(updatedReferenceIds);
          }}
        />
      )}
      {loadingReferenceError && (
        <AppSnackbar open severity='error'>
          Error getting references. Please check your internet connection and
          try again.
        </AppSnackbar>
      )}
      {assigningReferenceError && (
        <AppSnackbar open severity='error'>
          Error assigning reference
        </AppSnackbar>
      )}
    </Card>
  );
};

export default RiskReferenceCard;
